import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import PriorVisitHeader from "./proirvisitHeader";
import { PriorCardsGrid, PriorCardsList } from "./priorcards";
import { SummaryCardGrid, SummaryCardsList } from "./summarycard";
import { AudioCardGrid, Audiocards } from "./audiocards";
import dayjs from 'dayjs';

import CloseIcon from '@mui/icons-material/Close';
import { Backdrop, CircularProgress } from "@mui/material";
import api from "../../../../api/api";
import careiqapi from "../../../../api/careiqapi";
import RenderBlinkscribeRecords from "./blinkscribeRecords/blinkscribeRecords";
import { set } from "date-fns";
import { is } from "date-fns/locale";
import DropdownPill from "../Document/DropdownPill";
import ProcedureDocument from "../Document/ProcedureDocument";
import PaymentHistory from "./PaymentHistory";
import { getPracticeId } from "../../../../config/practice";
const formatDate = (dateString) => {
  return dayjs(dateString).format('YYYY-MM-DD');
};

/*
 * Athena call plan item 11 (10-15) + review 10-15.
 * Summary list from the v2 fhir-mirror encounter list (free FHIR):
 *  - no stale opt-in: Prior Visits keeps the blocking rebuild of an expired (> 24 h) list,
 *    as the owner chose for it on 10-05 (only the CareIQ tab serves old lists at once;
 *    the CareIQ tab's fetch-helper test guards it);
 *  - a list with no fresh cache makes the mirror build it from FHIR before answering
 *    (up to ~40 s for a long chart): after ENCOUNTER_LIST_TIMEOUT_MS the tab uses the
 *    old (billed, ~1-2 s) route instead; the mirror still finishes and keeps its list;
 *  - a list cached before the patient's latest visit day is served at once and rebuilt
 *    in the background (_list.refreshing): it is read once more after
 *    ENCOUNTER_REREAD_MS, so the new visit shows without a billed read.
 */
const ENCOUNTER_LIST_TIMEOUT_MS = 10000;
const ENCOUNTER_REREAD_MS = 15000;
const ENCOUNTER_MIRROR_OPTS = { timeout: ENCOUNTER_LIST_TIMEOUT_MS };
// Deleted encounters (CANCELLED), which the Athena list leaves out, stay out.
const HIDDEN_ENCOUNTER_STATUSES = ["CANCELLED", "CANCELED", "ENTERED-IN-ERROR"];
const visibleEncounters = (list) =>
  list.filter((e) => !HIDDEN_ENCOUNTER_STATUSES.includes(String(e?.status || "").toUpperCase()));
const isMirrorEncounterList = (res) => !!res && res.status === 200 && Array.isArray(res.data?.encounters);

const PriorVisit = ({internavtab ,searchdate ,dataId}) => {
  // Practice 9928 is two locations (1 = Odessa, 2 = Dallas/Frisco). This was
  // hardcoded to 1, so a Dallas patient's prior visits, progress notes and
  // BlinkScribe records were all fetched against Odessa.
  const userConfig = useSelector((state) => state.userconfig);
  const departmentId =
    Number(dataId?.departmentid) ||
    Number(userConfig?.config?.departmentid) ||
    1;
  const [view, setView] = useState(1);
  const [selectedTab, setSelectedTab] = useState(internavtab===false?0:internavtab);
  const [searchDate, setSearchDate] = useState(formatDate(searchdate));
  const [pData, setPData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [documentHtml, setDocumentHtml] = useState("");
  const practiseid = getPracticeId();
const [documentloading,setdocumentLoading]=useState(false)
const [isDocumentVisible, setDocumentVisible] = useState(false);
const [progessnotesdata,setProgressnotesData]=useState([])
const [progressnotesmodal,setProgressnotesModal]=useState(false)
const [selectedprocedure,setselectedProcedure]=useState({})
  const [selectedYear, setSelectedYear] = useState("all");
const [paymentHistory, setPaymentHistory] = useState([]);
const [isPaymentLoading, setIsPaymentLoading] = useState(false);
  // ✅ Manage selected range
const [selectedDateRange, setSelectedDateRange] = useState({
  startDate: dayjs().subtract(1, "month").toDate(), // 1 month ago
  endDate: dayjs().toDate(),                         // today
  key: "selection",
});
   // Fetch procedure data based on the selected search date
   const patientId=dataId.patientid

   /*
    * Summary list. The v2 fhir-mirror encounter list (free FHIR R4, the list the CareIQ
    * timeline reads) answers the fields the cards use (encounterid, encounterdate
    * MM/DD/YYYY, providerfirstname/lastname), newest first. It replaces the billed
    * Athena chart/{id}/encounters read (Athena call plan item 11); see the constants
    * above for stale lists, the re-read and the timeout. If the mirror fails or times
    * out, the old route once.
    */
   const encountersLoadedFor = useRef(null); // "patient|department" the list was loaded for
   const rereadTimer = useRef(null);
   const mountedRef = useRef(false);
   useEffect(() => {
     mountedRef.current = true;
     return () => {
       mountedRef.current = false;
       clearTimeout(rereadTimer.current);
     };
   }, []);
   const fetchPriorVisitData = async () => {
    setIsLoading(true);
    clearTimeout(rereadTimer.current);
    const loadedFor = `${patientId}|${departmentId}`;
    const mirrorUrl = `/v1/fhir-mirror/${practiseid}/patient/${patientId}/encounter`;
    try {
      const payload = {
       departmentid: departmentId
      };
      let response = null;
      try {
        response = await careiqapi.post(mirrorUrl, payload, ENCOUNTER_MIRROR_OPTS);
      } catch (mirrorError) {
        response = null;
      }
      const fromMirror = isMirrorEncounterList(response);
      if (!fromMirror) {
        response = await api.post(`/v1/${practiseid}/patient/${patientId}/encounter`, payload);
      }

      if (response.status === 200 && response.data.encounters) {
        setPData(visibleEncounters(response.data.encounters)); // Set encounters correctly
      } else {
        setPData([]); // Reset to empty array if no encounters
      }
      // A list being rebuilt: read it once more (free), keep what is shown on any failure.
      if (fromMirror && response._list?.refreshing && mountedRef.current) {
        rereadTimer.current = setTimeout(async () => {
          if (!mountedRef.current || encountersLoadedFor.current !== loadedFor) return;
          try {
            const again = await careiqapi.post(mirrorUrl, payload, ENCOUNTER_MIRROR_OPTS);
            if (mountedRef.current && encountersLoadedFor.current === loadedFor && isMirrorEncounterList(again)) {
              setPData(visibleEncounters(again.data.encounters));
            }
          } catch (rereadError) {
            /* keep the list shown */
          }
        }, ENCOUNTER_REREAD_MS);
      }
    } catch (error) {
      console.error("Error fetching procedure data:", error.response ? error.response.data : error);
    } finally {
      setIsLoading(false);
    }
  };

  // console.log('Prior Visit',pData)

  // Only the Summary sub-tab shows the encounter list: load it when that renders,
  // once per patient / department (encountersLoadedFor, declared above).
  useEffect(() => {
    if (selectedTab !== 0) return;
    const key = `${patientId}|${departmentId}`;
    if (encountersLoadedFor.current === key) return;
    encountersLoadedFor.current = key;
    fetchPriorVisitData();
  }, [patientId, departmentId, selectedTab]);
useEffect(() => {
  if (selectedTab === 1) {
    setSelectedDateRange({
      startDate: null,
      endDate: null,
      key: "selection",
    });
  }
}, [selectedTab]);
  const fetchDocument = async (encounterId) => {
  setdocumentLoading(true)
    console.log('encounterID',encounterId)
    try {
    const  payload={
        encounter_id:encounterId
      }

      // The note's document content always comes from the original route (Athena's
      // current summary, never a kept copy): the mirror's specific-summary keeps the
      // first copy that has an Assessment/Plan for good, so a draft or a note changed
      // later could show (review 10-15). Only the visit LIST above uses the mirror.
      const response = await api.post(`/v1/${practiseid}/encounter/specific-summary`,  payload );
  
      if (response.status === 200) {
         setDocumentHtml(response.data.summaryhtml); 
         setDocumentVisible(true);
      }
    } catch (error) {
      console.error("Error fetching document:", error.response ? error.response.data : error);
    }finally{
      setdocumentLoading(false)
    }
  };
     const fetchProgress = async () => {
    setIsLoading(true);
    try {
      // v2 mirror lists (same source as the Documents tab): AI labels,
      // local/faxed uploads and proprietary-only docs are all included —
      // the old v1 FHIR-only path missed those and showed raw filenames
      // (QA 2026-08-27).
      const cats = ["encounterdocument", "clinicaldocument", "imagingresult"];
      const results = await Promise.all(cats.map((cat) =>
        careiqapi.get(`/v1/fhir-mirror/${practiseid}/patients/${patientId}/documents/${cat}`)
          .then((r) => (r?.data?.[`${cat}s`] || r?.[`${cat}s`] || []))
          .catch(() => [])
      ));
      const merged = results.flat().map((d) => ({
        ...d,
        documentdescription: d.label || d.documentdescription || "-",
      }));
      setProgressnotesData({ encounterdocuments: merged });
    } catch (error) {
      console.error(
        "Error fetching procedure data:",
        error.response ? error.response.data : error
      );
      setProgressnotesData([]);
    } finally {
      setIsLoading(false);
    }
  };
   const fetchPayments = async () => {
      try {
        setIsPaymentLoading(true);
        const response = await api.get(
          `/v1/${practiseid}/pcard/${dataId?.patientid}/getPaymentDetails`
        );
        if (response.status === 200) {
          setPaymentHistory(response.data || []);
        } else {
          setPaymentHistory([]);
        }
      } catch (error) {
        console.log(error);
      } finally {
        setIsPaymentLoading(false);
      }
    };
useEffect(()=>{
  if(selectedTab==2){
    fetchProgress()
  }else if(selectedTab==3){
    fetchPayments()
  }
},[selectedTab])
 const yearOptions = useMemo(() => {
  const docs = progessnotesdata?.encounterdocuments || [];
  if (!Array.isArray(docs) || docs.length === 0)
    return [{ label: "All", value: "all" }];

  const years = docs
    .map((doc) => {
      // handle multiple possible date fields
      const dateValue =
        doc?.date ||
        doc?.created_at ||
        doc?.updated_at ||
        doc?.encounter_date ||
        doc?.createddate ||
        doc?.createddatetime ||
        doc?.lastmodifieddatetime;

      if (!dateValue) return null;
      const parsed = dayjs(dateValue);
      return parsed.isValid() ? parsed.year().toString() : null;
    })
    .filter(Boolean);

  const uniqueYears = Array.from(new Set(years)).sort((a, b) => b - a);

  return [
    { label: "All", value: "all" },
    ...uniqueYears.map((y) => ({ label: y, value: y })),
  ];
}, [progessnotesdata]);

const filteredDocs = useMemo(() => {
  if (!Array.isArray(progessnotesdata?.encounterdocuments)) return [];

  const docs = progessnotesdata.encounterdocuments;

  if (selectedYear === "all") return docs;

  return docs.filter((x) => {
    const dateValue =
      x?.date ||
      x?.created_at ||
      x?.updated_at ||
      x?.encounter_date ||
      x?.createddate ||
      x?.createddatetime ||
      x?.lastmodifieddatetime;

    if (!dateValue) return false;

    const parsed = dayjs(dateValue);
    if (!parsed.isValid()) return false;

    return parsed.year().toString() === selectedYear;
  });
}, [progessnotesdata, selectedYear]);
useEffect(() => {
    if (isDocumentVisible) {
      const sections = document.querySelectorAll(".section:not(.always-expanded)");
      sections.forEach((section) => {
        const header = section.querySelector(".section-header");
        if (!header.dataset.initialized) {
          header.addEventListener("click", () => {
            const content = section.querySelector(".section-content");
            const toggle = section.querySelector(".section-toggle");
            const isExpanded = header.classList.contains("expanded");

            if (isExpanded) {
              header.classList.remove("expanded");
              content.classList.remove("expanded");
              toggle.classList.remove("expanded");
            } else {
              header.classList.add("expanded");
              content.classList.add("expanded");
              toggle.classList.add("expanded");
            }
          });
          header.dataset.initialized = "true";
        }
      });
    }

    // Cleanup listeners when overlay closes
    return () => {
      const sections = document.querySelectorAll(".section:not(.always-expanded)");
      sections.forEach((section) => {
        const header = section.querySelector(".section-header");
        if (header.dataset.initialized) {
          header.replaceWith(header.cloneNode(true));
        }
      });
    };
  }, [isDocumentVisible, documentHtml]);
  const renderOverlay = () => {
    if (!isDocumentVisible) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
        <div
          className="bg-white relative w-[80%] overflow-y-auto h-[95vh]"
          style={{ scrollbarWidth: 'none' }}
        >
          <div className="sticky z-10 pr-2 bg-white top-0 w-full flex justify-end">
            <button onClick={() => setDocumentVisible(false)} className="mt-2 rounded">
              <CloseIcon style={{ width: "30px" }} />
            </button>
          </div>
          <div className="p-4">
            <div dangerouslySetInnerHTML={{ __html: documentHtml }} />
          </div>
        </div>
      </div>
    );
  };
  

  const tabs = [
    { label: "Summary" },
    { label: "Audio" },
    { label: "Progress Notes" },
    { label: "Special Tab" } 
  ];
  const Handletoggle=(value)=>{
    setView(value)
  }
  // Group by year
const groupedByYear = useMemo(() => {
  if (!filteredDocs || filteredDocs.length === 0) return {};
  return filteredDocs.reduce((acc, doc) => {
    const dateValue =
      doc?.date ||
      doc?.created_at ||
      doc?.updated_at ||
      doc?.encounter_date ||
      doc?.createddate ||
      doc?.createddatetime ||
      doc?.lastmodifieddatetime;
    const year = dayjs(dateValue).isValid()
      ? dayjs(dateValue).year().toString()
      : "Unknown";
    if (!acc[year]) acc[year] = [];
    acc[year].push(doc);
    return acc;
  }, {});
}, [filteredDocs]);

// Sort years descending
const sortedYears = Object.keys(groupedByYear).sort((a, b) => b - a);

  const renderContent = () => {
    switch (selectedTab) {
      case 0:
      
        return (
          <div className="flex flex-col gap-4 overflow-auto max-h-screen p-4">
               <Backdrop
              sx={{
                color: '#fff',
                zIndex: (theme) => theme.zIndex.drawer + 1,
                backgroundColor: 'rgba(0, 0, 0, 0.7)', // reduced opacity (default is 0.5)
              }}
              open={documentloading}
            >
              <CircularProgress color="inherit" />
            </Backdrop>
          {!view ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 h-[450px] pt-5">
               {isLoading ? ( // Check if loading
                 <div className="w-[100%] flex justify-end items-center">
                 <CircularProgress />
                 </div>
                ) : pData.length > 0 ? (
                  pData.map((encounter) => (
                    <SummaryCardGrid
                      key={encounter.encounterid}
                      encounter={encounter}
                      onViewDocument={fetchDocument}
                    />
                  ))
                ) : (
                  <p>No encounters found.</p>
                )}
            </div>
          ) : (
            <div className="flex flex-col gap-4 h-[430px] ">
               {isLoading ? ( // Check if loading
                  <p>Loading...</p> // Or replace with a spinner
                ) : pData.length > 0 ? (
                  pData.map((encounter) => (
                    <SummaryCardsList
                      key={encounter.encounterid}
                      encounter={encounter}
                      onViewDocument={fetchDocument}
                    />
                  ))
                ) : (
                  <p>No encounters found.</p>
                )}
            </div>
          )}
        </div>
        );


      case 1:
      
        return (
          <>
             <div className="flex flex-col gap-4 h-[430px] ">
                 <RenderBlinkscribeRecords view={view} data={dataId}     setSelectedDateRange={setSelectedDateRange}
        selectedDateRange={selectedDateRange}    searchDate={searchDate} internavtab={selectedTab}/>
              </div>
         
          </>
        
        );
      case 2:
    
        return (
         <div className="flex flex-col h-screen">
  {/* Fixed Dropdown */}
  <div className="flex justify-start mb-2 px-4 pt-4 flex-shrink-0  z-10">
    <DropdownPill
      options={yearOptions}
      value={selectedYear}
      onChange={setSelectedYear}
      placeholder="Select Year"
    />
  </div>

  {/* Scrollable Content */}
  <div className="flex-1 overflow-auto px-4 pb-4">
    {view ? (
      <div className="flex flex-col gap-4">
        {filteredDocs.length > 0 ? (
          filteredDocs
            .slice()
            .sort((a, b) => {
              // Sort by most recent date (descending)
              const getDate = (d) =>
                dayjs(
                  d?.date ||
                    d?.created_at ||
                    d?.updated_at ||
                    d?.encounter_date ||
                    d?.createddate ||
                    d?.createddatetime ||
                    d?.lastmodifieddatetime
                );
              return getDate(b).valueOf() - getDate(a).valueOf();
            })
            .map((x, i) => <PriorCardsList key={i} setIsProcedureDocumentOpen={setProgressnotesModal} setselectedProcedure={setselectedProcedure} x={x} />)
        ) : !isLoading ? (
          <div className="col-span-2 text-center text-gray-500 py-6">
            No documents found
          </div>
        ) : null}
      </div>
    ) : (
   <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
  {sortedYears.length > 0 ? (
    sortedYears.map((year) => (
      <PriorCardsGrid key={year} year={year} setselectedProcedure={setselectedProcedure} setIsProcedureDocumentOpen={setProgressnotesModal}  docs={groupedByYear[year]} />
    ))
  ) : (
    <div className="col-span-2 text-center text-gray-500 py-6">
      No documents found
    </div>
  )}
</div>
    )}
  </div>
</div>

        );
      case 3:
        return (
          <div className="flex flex-col h-screen overflow-auto">
            <PaymentHistory payments={paymentHistory} isLoading={isPaymentLoading} />
          </div>
        );
      default:
        return null;
    }
  };

 

  return (
    <div className="flex flex-col justify-start h-full">
      <PriorVisitHeader 
        viewStatus={view} 
        handleView={Handletoggle} 
        tabs={tabs} 
        selectedTab={selectedTab} 
        onSelectTab={setSelectedTab} 
        searchDate={searchDate}
        setSearchDate={setSearchDate}
        setSelectedDateRange={setSelectedDateRange}
        selectedDateRange={selectedDateRange}
      />
      {renderContent()}
      <ProcedureDocument
                  subview={2}
                  isProcedureDocumentOpen={progressnotesmodal}
                  setIsProcedureDocumentOpen={setProgressnotesModal}
                  procedure={{ ...selectedprocedure, patientid: selectedprocedure?.patientid ?? dataId?.patientid }}
                />
     { renderOverlay()}
    </div>
  );
};

export default PriorVisit;
